import { Coding, Observation, ObservationComponent } from "@medplum/fhirtypes";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Extensions } from "@/fhir/enums/extensions";
import { QuiltedHealthCodes } from "@/fhir/enums/quilted-health";

import { RootState } from "..";

interface BirthState {
  observation?: Observation;
  birthDateTime?: string;
  sourceFormType?: string; // Type of form that updated birth time (birthing/shoulderDystocia)
  sourceLaborStageId?: string; // ID of the labor stage the source form is in
  isOptimistic?: boolean; // Flag to track if the current state is optimistic
}

const initialState: BirthState = {
  observation: undefined,
  birthDateTime: undefined,
  sourceFormType: undefined,
  sourceLaborStageId: undefined,
  isOptimistic: false,
};

/**
 * Updates `birthDateTime` and source fields from `state.observation` only (does not assign
 * `observation`). Matches `setBirthObservations` semantics: missing observation clears source
 * fields but preserves `birthDateTime`; canonical without `valueDateTime` leaves prior birth time.
 */
function syncBirthDerivedFieldsFromObservation(state: BirthState) {
  const obs = state.observation;
  if (!obs) {
    state.sourceFormType = undefined;
    state.sourceLaborStageId = undefined;
    return;
  }

  if (
    obs.code?.coding?.some((coding: Coding) => coding.code === QuiltedHealthCodes.BirthDateTime)
  ) {
    if (obs.valueDateTime) {
      state.birthDateTime = obs.valueDateTime;
    }

    const sourceTypeExtension = obs.extension?.find(
      (ext) => ext.url === Extensions.QhBirthSourceType,
    );
    const sourceLaborStageExtension = obs.extension?.find(
      (ext) => ext.url === Extensions.QhBirthSourceLaborStage,
    );

    state.sourceFormType = sourceTypeExtension?.valueString;
    state.sourceLaborStageId = sourceLaborStageExtension?.valueReference?.reference?.split("/")[1];
    return;
  }

  const legacyBirthDateTime = obs.component?.find((c: ObservationComponent) =>
    c.code?.coding?.some((coding: Coding) => coding.code === "body-delivery-date-time"),
  )?.valueDateTime;
  if (legacyBirthDateTime) {
    state.birthDateTime = legacyBirthDateTime;
  }
  state.sourceFormType = undefined;
  state.sourceLaborStageId = undefined;
}

const birthSlice = createSlice({
  name: "birth",
  initialState,
  reducers: {
    setBirthObservations: (state, action: PayloadAction<Observation[]>) => {
      const observations = action.payload;
      const canonicalBirthObservationWithDateTime = observations.find(
        (observation) =>
          observation.valueDateTime &&
          observation.code?.coding?.some(
            (coding: Coding) => coding.code === QuiltedHealthCodes.BirthDateTime,
          ),
      );
      const canonicalBirthObservation = observations.find((observation) =>
        observation.code?.coding?.some(
          (coding: Coding) => coding.code === QuiltedHealthCodes.BirthDateTime,
        ),
      );
      const legacyBirthObservationWithDateTime = observations.find((observation) =>
        observation.component?.some(
          (component: ObservationComponent) =>
            component.code?.coding?.some(
              (coding: Coding) => coding.code === "body-delivery-date-time",
            ) && component.valueDateTime,
        ),
      );
      const legacyBirthObservation = observations.find((observation) =>
        observation.component?.some((component: ObservationComponent) =>
          component.code?.coding?.some(
            (coding: Coding) => coding.code === "body-delivery-date-time",
          ),
        ),
      );
      const obs =
        canonicalBirthObservationWithDateTime ??
        legacyBirthObservationWithDateTime ??
        canonicalBirthObservation ??
        legacyBirthObservation ??
        observations[0];
      state.observation = obs;
      syncBirthDerivedFieldsFromObservation(state);

      state.isOptimistic = false; // Clear optimistic flag when real data arrives
    },
    setBirthDateTime: (state, action: PayloadAction<string | undefined>) => {
      state.birthDateTime = action.payload;
    },
    // New action for optimistic updates
    setOptimisticBirthDateTime: (state, action: PayloadAction<string>) => {
      const next = action.payload;
      // Form prefill can echo the same canonical birth time; avoid flipping to optimistic so a
      // later empty sync cannot clear server-backed birth via `clearOptimisticBirthDateTime`.
      if (state.birthDateTime === next && !state.isOptimistic) {
        return;
      }
      state.birthDateTime = next;
      state.isOptimistic = true;
    },
    clearBirthState: (state) => {
      state.observation = undefined;
      state.birthDateTime = undefined;
      state.sourceFormType = undefined;
      state.sourceLaborStageId = undefined;
      state.isOptimistic = false;
    },
    /** Clears optimistic client edits; if a persisted birth observation remains, re-sync derived fields from it. */
    clearOptimisticBirthDateTime: (state) => {
      if (!state.isOptimistic) {
        return;
      }
      state.isOptimistic = false;
      if (state.observation) {
        syncBirthDerivedFieldsFromObservation(state);
      } else {
        state.birthDateTime = undefined;
        state.sourceFormType = undefined;
        state.sourceLaborStageId = undefined;
      }
    },
  },
});

export const {
  setBirthObservations,
  setBirthDateTime,
  setOptimisticBirthDateTime,
  clearBirthState,
  clearOptimisticBirthDateTime,
} = birthSlice.actions;

export const selectCurrentBirth = (state: RootState) => state.birth.observation;
export const selectBirthDateTime = (state: RootState) => state.birth.birthDateTime;
export const selectBirthSourceFormType = (state: RootState) => state.birth.sourceFormType;
export const selectBirthSourceLaborStageId = (state: RootState) => state.birth.sourceLaborStageId;
export const selectIsOptimisticBirth = (state: RootState) => state.birth.isOptimistic;

export default birthSlice.reducer;
